import React, { createContext, useContext, useState, useEffect } from 'react';
import { identifyUser, setUserProperties, trackDoseWiseEvents } from '../utils/analytics';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  userType: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [userType, setUserType] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE_URL}/verify-token`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
      .then(async response => {
        if (response.ok) {
          setIsAuthenticated(true);
          // Fetch user profile to get userType
          const userRes = await fetch(`${API_BASE_URL}/user/profile`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (userRes.ok) {
            const userData = await userRes.json();
            setUser(userData);
            setUserType(userData.userType || null);
            
            // Identify user in analytics
            identifyUser(userData._id, {
              email: userData.email,
              country: userData.country,
              userType: userData.userType,
              registrationDate: userData.createdAt
            });

            setUserProperties({
              lastLoginDate: new Date().toISOString(),
              totalPens: userData.pens?.length || 0,
              goldenDoseEnabled: userData.goldenDoseEnabled || false
            });
          }
        } else {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setUser(null);
          setUserType(null);
        }
      })
      .catch(() => {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setUser(null);
        setUserType(null);
      });
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('token', data.token);
      setIsAuthenticated(true);
      // Fetch user profile to get userType
      const userRes = await fetch(`${API_BASE_URL}/user/profile`, {
        headers: { 'Authorization': `Bearer ${data.token}` }
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
        setUserType(userData.userType || null);
        
        // Identify user in analytics
        identifyUser(userData._id, {
          email: userData.email,
          country: userData.country,
          userType: userData.userType,
          registrationDate: userData.createdAt
        });

        setUserProperties({
          lastLoginDate: new Date().toISOString(),
          totalPens: userData.pens?.length || 0,
          goldenDoseEnabled: userData.goldenDoseEnabled || false
        });
        
        // Track login event
        trackDoseWiseEvents.userLoggedIn('password');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      await response.json();
      
      // Track registration event
      trackDoseWiseEvents.userRegistered('email');
      
      await login(email, password);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setUserType(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, userType, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 